import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Copy, Download, Upload, RefreshCw, Check, X, AlertCircle, ChevronRight, ChevronDown, Search, Zap, Eye, EyeOff, ArrowUpDown, Hash, Type, Quote, Braces, Brackets, ToggleLeft, Minimize2, Maximize2, FileText, Settings, Filter, Code, Database, Globe, Calendar, MapPin, Link, Mail, Phone } from 'lucide-react';

interface YamlFormatterProps {}

type ViewMode = 'formatted' | 'tree';
type DataType = 'string' | 'number' | 'integer' | 'float' | 'boolean' | 'null' | 'array' | 'object' | 'email' | 'url' | 'date' | 'phone';

interface SearchResult {
  path: string;
  key: string;
  value: any;
  type: DataType;
}

interface StackItem {
  obj: any;
  indent: number;
}

const YamlFormatter: React.FC<YamlFormatterProps> = () => {
  const [input, setInput] = useState<string>('');
  const [parsedData, setParsedData] = useState<any>(null);
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [error, setError] = useState<string>('');
  const [copied, setCopied] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState<number>(0);
  const lineNumberRef = useRef<number>(1);
  
  // View options
  const [viewMode, setViewMode] = useState<ViewMode>('tree');
  const [indentSize, setIndentSize] = useState<number>(2);
  const [sortKeys, setSortKeys] = useState<boolean>(false);
  const [showLineNumbers, setShowLineNumbers] = useState<boolean>(true);
  const [showDataTypes, setShowDataTypes] = useState<boolean>(true);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const [allExpanded, setAllExpanded] = useState<boolean>(true);
  const [showQuotes, setShowQuotes] = useState<boolean>(false);
  const [highlightSyntax, setHighlightSyntax] = useState<boolean>(true);
  const [compactArrays, setCompactArrays] = useState<boolean>(false);
  const [filterByType, setFilterByType] = useState<string>('all');
  const [highlightSearchResults, setHighlightSearchResults] = useState<boolean>(true);
  const [caseSensitiveSearch, setCaseSensitiveSearch] = useState<boolean>(false);
  const [searchInKeys, setSearchInKeys] = useState<boolean>(true);
  const [searchInValues, setSearchInValues] = useState<boolean>(true);

  // Simple YAML parser (basic implementation)
  const parseYaml = useCallback((yamlString: string): any => {
    if (!yamlString.trim()) return null;
    
    try {
      // Remove comments
      const lines = yamlString.split('\n').map(line => {
        const commentIndex = line.indexOf('#');
        if (commentIndex !== -1 && !isInQuotes(line, commentIndex)) {
          return line.substring(0, commentIndex).trimEnd();
        }
        return line;
      }).filter(line => line.trim() !== '');

      return parseYamlLines(lines);
    } catch (error) {
      throw new Error(`YAML parsing error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, []);

  const isInQuotes = useCallback((line: string, position: number): boolean => {
    let inSingleQuote = false;
    let inDoubleQuote = false;
    let escaped = false;

    for (let i = 0; i < position; i++) {
      const char = line[i];
      if (escaped) {
        escaped = false;
        continue;
      }
      if (char === '\\') {
        escaped = true;
        continue;
      }
      if (char === '"' && !inSingleQuote) {
        inDoubleQuote = !inDoubleQuote;
      } else if (char === "'" && !inDoubleQuote) {
        inSingleQuote = !inSingleQuote;
      }
    }

    return inSingleQuote || inDoubleQuote;
  }, []);

  const parseYamlLines = useCallback((lines: string[]): any => {
    const result: any = {};
    const stack: StackItem[] = [{ obj: result, indent: -1 }];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const indent = line.length - line.trimStart().length;
      const trimmed = line.trim();
      
      if (!trimmed) continue;

      // Pop stack until we find the right parent
      while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
        stack.pop();
      }

      const parent = stack[stack.length - 1].obj;

      if (trimmed.startsWith('- ')) {
        // Array item
        const value = trimmed.substring(2).trim();
        if (!Array.isArray(parent)) {
          // Convert parent to array if needed
          const keys = Object.keys(parent);
          if (keys.length > 0) {
            throw new Error(`Mixed array and object syntax at line ${i + 1}`);
          }
          // Replace parent content with array
          const grandParent = stack[stack.length - 2];
          if (grandParent) {
            const parentKey = Object.keys(grandParent.obj).find(k => grandParent.obj[k] === parent);
            if (parentKey) {
              grandParent.obj[parentKey] = [];
              stack[stack.length - 1].obj = grandParent.obj[parentKey];
            }
          }
        }
        
        const currentArray = stack[stack.length - 1].obj;
        if (value.includes(':')) {
          // Object in array
          const obj = parseKeyValue(value);
          currentArray.push(obj);
          stack.push({ obj: obj, indent });
        } else {
          // Simple value in array
          currentArray.push(parseValue(value));
        }
      } else if (trimmed.includes(':')) {
        // Key-value pair
        const colonIndex = trimmed.indexOf(':');
        const key = trimmed.substring(0, colonIndex).trim();
        const value = trimmed.substring(colonIndex + 1).trim();
        
        if (value === '' || value === '|' || value === '>') {
          // Object or multiline string
          parent[key] = {};
          stack.push({ obj: parent[key], indent });
        } else {
          parent[key] = parseValue(value);
        }
      }
    }

    return Object.keys(result).length === 1 && Array.isArray(Object.values(result)[0]) 
      ? Object.values(result)[0] 
      : result;
  }, []);

  const parseKeyValue = useCallback((str: string): any => {
    const obj: any = {};
    const colonIndex = str.indexOf(':');
    if (colonIndex !== -1) {
      const key = str.substring(0, colonIndex).trim();
      const value = str.substring(colonIndex + 1).trim();
      obj[key] = parseValue(value);
    }
    return obj;
  }, []);

  const parseValue = useCallback((value: string): any => {
    if (value === 'null' || value === '~' || value === '') return null;
    if (value === 'true') return true;
    if (value === 'false') return false;
    
    // Remove quotes if present
    if ((value.startsWith('"') && value.endsWith('"')) || 
        (value.startsWith("'") && value.endsWith("'"))) {
      return value.slice(1, -1);
    }
    
    // Try to parse as number
    if (/^-?\d+$/.test(value)) {
      return parseInt(value, 10);
    }
    if (/^-?\d+\.\d+$/.test(value)) {
      return parseFloat(value);
    }
    
    return value;
  }, []);

  // Convert object to YAML
  const toYaml = useCallback((obj: any, indent: number = 0): string => {
    const spaces = ' '.repeat(indent);
    
    if (obj === null || obj === undefined) {
      return 'null';
    }
    
    if (typeof obj === 'string') {
      // Check if string needs quotes
      const needsQuotes = obj.includes(':') || obj.includes('#') || 
                         obj.includes('[') || obj.includes(']') ||
                         obj.includes('{') || obj.includes('}') ||
                         obj.trim() !== obj || 
                         ['true', 'false', 'null', '~'].includes(obj.toLowerCase()) ||
                         /^-?\d+(\.\d+)?$/.test(obj) ||
                         showQuotes;
      
      return needsQuotes ? `"${obj.replace(/"/g, '\\"')}"` : obj;
    }
    
    if (typeof obj === 'number' || typeof obj === 'boolean') {
      return String(obj);
    }
    
    if (Array.isArray(obj)) {
      if (obj.length === 0) return '[]';
      
      if (compactArrays && obj.every(item => typeof item !== 'object' || item === null)) {
        return `[${obj.map(item => toYaml(item, 0)).join(', ')}]`;
      }
      
      return obj.map(item => {
        if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
          const itemYaml = toYaml(item, indent + indentSize);
          const lines = itemYaml.split('\n');
          return `${spaces}- ${lines[0]}\n${lines.slice(1).join('\n')}`;
        } else {
          return `${spaces}- ${toYaml(item, 0)}`;
        }
      }).join('\n');
    }
    
    if (typeof obj === 'object') {
      const entries = Object.entries(obj);
      if (entries.length === 0) return '{}';
      
      const processedEntries = sortKeys 
        ? entries.sort(([a], [b]) => a.localeCompare(b))
        : entries;
      
      return processedEntries.map(([key, value]) => {
        const keyStr = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key) ? key : `"${key}"`;
        
        if (typeof value === 'object' && value !== null) {
          const valueYaml = toYaml(value, indent + indentSize);
          return `${spaces}${keyStr}:\n${valueYaml}`;
        } else {
          return `${spaces}${keyStr}: ${toYaml(value, 0)}`;
        }
      }).join('\n');
    }
    
    return String(obj);
  }, [indentSize, showQuotes, compactArrays, sortKeys]);

  const validateYaml = useCallback((yamlString: string) => {
    if (!yamlString.trim()) {
      setParsedData(null);
      setIsValid(null);
      setError('');
      setSearchResults([]);
      return;
    }

    try {
      const parsed = parseYaml(yamlString);
      setParsedData(parsed);
      setIsValid(true);
      setError('');
      
      if (allExpanded) {
        const paths = getAllPaths(parsed);
        setExpandedPaths(new Set(paths));
      }

      // Update search results when data changes
      if (searchTerm) {
        performSearch(searchTerm, parsed);
      }
    } catch (err) {
      setIsValid(false);
      const errorMsg = err instanceof Error ? err.message : 'Invalid YAML';
      setError(errorMsg);
      setParsedData(null);
      setSearchResults([]);
    }
  }, [allExpanded, searchTerm, parseYaml]);

  const getAllPaths = useCallback((obj: any, path: string = ''): string[] => {
    const paths: string[] = [];
    
    if (obj && typeof obj === 'object') {
      paths.push(path);
      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          paths.push(...getAllPaths(item, `${path}[${index}]`));
        });
      } else {
        Object.keys(obj).forEach(key => {
          paths.push(...getAllPaths(obj[key], path ? `${path}.${key}` : key));
        });
      }
    }
    
    return paths;
  }, []);

  const getAdvancedDataType = useCallback((value: any): DataType => {
    if (value === null) return 'null';
    if (Array.isArray(value)) return 'array';
    if (typeof value === 'object') return 'object';
    if (typeof value === 'boolean') return 'boolean';
    if (typeof value === 'number') return Number.isInteger(value) ? 'integer' : 'float';
    if (typeof value === 'string') {
      // Advanced type detection
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const urlRegex = /^https?:\/\/.+/;
      const phoneRegex = /^\+?[\d\s\-\(\)]{10,}$/;
      const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
      
      if (emailRegex.test(value)) return 'email';
      if (urlRegex.test(value)) return 'url';
      if (dateRegex.test(value)) return 'date';
      if (phoneRegex.test(value)) return 'phone';
      return 'string';
    }
    return 'string';
  }, []);

  const getTypeColor = useCallback((type: DataType): string => {
    const colors = {
      string: 'text-green-700 bg-green-100 border-green-200',
      integer: 'text-blue-700 bg-blue-100 border-blue-200',
      float: 'text-indigo-700 bg-indigo-100 border-indigo-200',
      number: 'text-blue-700 bg-blue-100 border-blue-200',
      boolean: 'text-purple-700 bg-purple-100 border-purple-200',
      null: 'text-gray-600 bg-gray-100 border-gray-200',
      array: 'text-orange-700 bg-orange-100 border-orange-200',
      object: 'text-red-700 bg-red-100 border-red-200',
      email: 'text-cyan-700 bg-cyan-100 border-cyan-200',
      url: 'text-teal-700 bg-teal-100 border-teal-200',
      date: 'text-amber-700 bg-amber-100 border-amber-200',
      phone: 'text-pink-700 bg-pink-100 border-pink-200'
    };
    return colors[type] || colors.string;
  }, []);

  const getTypeIcon = useCallback((type: DataType) => {
    const icons = {
      string: <Quote className="w-3 h-3" />,
      integer: <Hash className="w-3 h-3" />,
      float: <Hash className="w-3 h-3" />,
      number: <Hash className="w-3 h-3" />,
      boolean: <ToggleLeft className="w-3 h-3" />,
      null: <X className="w-3 h-3" />,
      array: <Brackets className="w-3 h-3" />,
      object: <Braces className="w-3 h-3" />,
      email: <Mail className="w-3 h-3" />,
      url: <Link className="w-3 h-3" />,
      date: <Calendar className="w-3 h-3" />,
      phone: <Phone className="w-3 h-3" />
    };
    return icons[type] || <Type className="w-3 h-3" />;
  }, []);

  const performSearch = useCallback((term: string, data: any = parsedData) => {
    if (!term || !data) {
      setSearchResults([]);
      return;
    }

    const results: SearchResult[] = [];
    const searchLower = caseSensitiveSearch ? term : term.toLowerCase();

    const searchObject = (obj: any, path: string = '') => {
      if (Array.isArray(obj)) {
        obj.forEach((item, index) => {
          const currentPath = `${path}[${index}]`;
          searchObject(item, currentPath);
        });
      } else if (obj && typeof obj === 'object') {
        Object.entries(obj).forEach(([key, value]) => {
          const currentPath = path ? `${path}.${key}` : key;
          
          // Search in keys
          if (searchInKeys) {
            const keyToSearch = caseSensitiveSearch ? key : key.toLowerCase();
            if (keyToSearch.includes(searchLower)) {
              results.push({
                path: currentPath,
                key,
                value,
                type: getAdvancedDataType(value)
              });
            }
          }

          // Search in values
          if (searchInValues && value !== null && typeof value !== 'object') {
            const valueToSearch = caseSensitiveSearch ? String(value) : String(value).toLowerCase();
            if (valueToSearch.includes(searchLower)) {
              results.push({
                path: currentPath,
                key,
                value,
                type: getAdvancedDataType(value)
              });
            }
          }

          // Continue recursive search
          if (typeof value === 'object' && value !== null) {
            searchObject(value, currentPath);
          }
        });
      }
    };

    searchObject(data);
    setSearchResults(results);
    setCurrentSearchIndex(0);

    // Auto-expand paths containing search results
    if (results.length > 0) {
      const newExpanded = new Set(expandedPaths);
      results.forEach(result => {
        const pathParts = result.path.split(/[.\[\]]+/).filter(Boolean);
        let currentPath = '';
        pathParts.forEach((part, index) => {
          if (index === 0) {
            currentPath = part;
          } else {
            currentPath += pathParts[index-1]?.match(/\d+/) ? `[${part}]` : `.${part}`;
          }
          newExpanded.add(currentPath);
        });
      });
      setExpandedPaths(newExpanded);
    }
  }, [parsedData, caseSensitiveSearch, searchInKeys, searchInValues, expandedPaths, getAdvancedDataType]);

  useEffect(() => {
    performSearch(searchTerm);
  }, [searchTerm, performSearch]);

  const sortObjectDeep = useCallback((obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(sortObjectDeep);
    } else if (obj && typeof obj === 'object') {
      return Object.keys(obj)
        .sort()
        .reduce((sorted: any, key: string) => {
          sorted[key] = sortObjectDeep(obj[key]);
          return sorted;
        }, {});
    }
    return obj;
  }, []);

  const getFormattedYaml = useMemo(() => {
    if (!parsedData) return '';
    const dataToFormat = sortKeys ? sortObjectDeep(parsedData) : parsedData;
    return toYaml(dataToFormat);
  }, [parsedData, sortKeys, toYaml, sortObjectDeep]);

  const getMinifiedYaml = useMemo(() => {
    if (!parsedData) return '';
    const dataToFormat = sortKeys ? sortObjectDeep(parsedData) : parsedData;
    return toYaml(dataToFormat, 0).replace(/\n\s*/g, ' ').replace(/\s+/g, ' ').trim();
  }, [parsedData, sortKeys, toYaml, sortObjectDeep]);

  const togglePath = useCallback((path: string) => {
    const newExpanded = new Set(expandedPaths);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedPaths(newExpanded);
  }, [expandedPaths]);

  const expandAll = useCallback(() => {
    const allPaths = getAllPaths(parsedData);
    setExpandedPaths(new Set(allPaths));
    setAllExpanded(true);
  }, [parsedData, getAllPaths]);

  const collapseAll = useCallback(() => {
    setExpandedPaths(new Set());
    setAllExpanded(false);
  }, []);

  const isSearchMatch = useCallback((path: string, key: string): boolean => {
    if (!searchTerm) return false;
    return searchResults.some(result => 
      result.path === path && result.key === key
    );
  }, [searchTerm, searchResults]);

  const TreeNode: React.FC<{
    data: any;
    keyName?: string;
    path: string;
    level: number;
    isLast?: boolean;
    parentIsArray?: boolean;
  }> = ({ data, keyName, path, level, isLast = false, parentIsArray = false }) => {
    const type = getAdvancedDataType(data);
    const isObject = data && typeof data === 'object' && !Array.isArray(data);
    const isArray = Array.isArray(data);
    const isExpandable = isObject || isArray;
    const isExpanded = expandedPaths.has(path);
    const hasChildren = isExpandable && 
      ((isArray && data.length > 0) || (isObject && Object.keys(data).length > 0));

    const shouldShow = filterByType === 'all' || filterByType === type;
    const isHighlighted = highlightSearchResults && isSearchMatch(path, keyName || '');

    if (!shouldShow) return null;

    const currentLineNumber = lineNumberRef.current++;

    const getValueDisplay = () => {
      if (data === null) return <span className="text-gray-500">null</span>;
      if (typeof data === 'string') {
        const colorClass = type === 'email' ? 'text-cyan-600' : 
                          type === 'url' ? 'text-teal-600' :
                          type === 'date' ? 'text-amber-600' :
                          type === 'phone' ? 'text-pink-600' : 'text-green-600';
        const needsQuotes = showQuotes || 
                           data.includes(':') || data.includes('#') || 
                           ['true', 'false', 'null'].includes(data.toLowerCase());
        return (
          <span className={colorClass}>
            {needsQuotes ? `"${data}"` : data}
          </span>
        );
      }
      if (typeof data === 'number') return <span className="text-blue-600">{data}</span>;
      if (typeof data === 'boolean') return <span className="text-purple-600">{data.toString()}</span>;
      return null;
    };

    const renderChildren = () => {
      if (!hasChildren || !isExpanded) return null;

      const children = [];

      if (isArray) {
        for (let index = 0; index < data.length; index++) {
          children.push(
            <TreeNode
              key={`${path}[${index}]`}
              data={data[index]}
              keyName={index.toString()}
              path={`${path}[${index}]`}
              level={level + 1}
              isLast={index === data.length - 1}
              parentIsArray={true}
            />
          );
        }
      } else {
        const entries = Object.entries(data);
        entries.forEach(([key, value], index) => {
          children.push(
            <TreeNode
              key={`${path}.${key}`}
              data={value}
              keyName={key}
              path={path ? `${path}.${key}` : key}
              level={level + 1}
              isLast={index === entries.length - 1}
              parentIsArray={false}
            />
          );
        });
      }

      return children;
    };

    return (
      <>
        <div className={`flex items-center py-1 group font-mono text-sm transition-colors ${
          isHighlighted ? 'bg-yellow-100 border-l-4 border-yellow-500' : 'hover:bg-blue-50'
        }`}>
          {/* Line Number */}
          {showLineNumbers && (
            <div className="w-12 text-right text-gray-400 text-xs mr-3 select-none flex-shrink-0">
              {currentLineNumber}
            </div>
          )}

          <div 
            className="flex items-center flex-1 min-w-0"
            style={{ paddingLeft: `${level * 20}px` }}
          >
            {/* Expand/Collapse Button */}
            {hasChildren ? (
              <button
                onClick={() => togglePath(path)}
                className="flex items-center justify-center w-4 h-4 mr-2 hover:bg-blue-200 rounded text-gray-600 flex-shrink-0"
              >
                {isExpanded ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
              </button>
            ) : (
              <div className="w-4 mr-2 flex-shrink-0" />
            )}

            {/* Array marker for YAML */}
            {parentIsArray && (
              <span className="text-gray-600 mr-2 flex-shrink-0">-</span>
            )}

            {/* Key Name */}
            {keyName !== undefined && !parentIsArray && (
              <span className="mr-2 flex-shrink-0">
                <span className="text-blue-800 font-medium">{keyName}:</span>
              </span>
            )}

            {/* Data Type Badge */}
            {showDataTypes && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium mr-2 border flex-shrink-0 ${getTypeColor(type)}`}>
                {getTypeIcon(type)}
                {type}
              </span>
            )}

            {/* Value */}
            <div className="flex-1 min-w-0">
              {!isExpandable ? (
                <span className="truncate block">{getValueDisplay()}</span>
              ) : (
                <span className="text-gray-600">
                  {isArray ? (
                    <>
                      {!isExpanded && (
                        <span className="text-xs text-gray-500">
                          [{data.length} {data.length === 1 ? 'item' : 'items'}]
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      {!isExpanded && (
                        <span className="text-xs text-gray-500">
                          {`{${Object.keys(data).length} ${Object.keys(data).length === 1 ? 'key' : 'keys'}}`}
                        </span>
                      )}
                    </>
                  )}
                </span>
              )}
            </div>

            {/* Path for debugging */}
            <span className="ml-2 text-xs text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 max-w-32 truncate">
              {path || 'root'}
            </span>
          </div>
        </div>

        {/* Children */}
        {renderChildren()}
      </>
    );
  };

  const renderTreeView = () => {
    lineNumberRef.current = 1; // Reset line counter
    return (
      <div className="p-3 overflow-auto h-full">
        {parsedData && <TreeNode data={parsedData} path="" level={0} />}
      </div>
    );
  };

  const highlightYamlLine = useCallback((line: string): string => {
    let highlighted = line
      .replace(/^(\s*)([^:\s]+):/g, '$1<span style="color: #0066cc; font-weight: 600;">$2:</span>')
      .replace(/:\s*"([^"]*)"/g, ': <span style="color: #008000;">"$1"</span>')
      .replace(/:\s*([^"#\s][^#]*?)(\s*#.*)?$/g, ': <span style="color: #008000;">$1</span>$2')
      .replace(/:\s*(true|false|null|~)/g, ': <span style="color: #800080;">$1</span>')
      .replace(/:\s*(-?\d+\.?\d*)/g, ': <span style="color: #0066cc;">$1</span>')
      .replace(/^(\s*)-\s*/g, '$1<span style="color: #800080; font-weight: bold;">- </span>')
      .replace(/(#.*$)/g, '<span style="color: #888; font-style: italic;">$1</span>');

    // Highlight search terms
    if (searchTerm && highlightSearchResults) {
      const searchRegex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, caseSensitiveSearch ? 'g' : 'gi');
      highlighted = highlighted.replace(searchRegex, '<mark style="background-color: #ffeb3b; color: #000;">$1</mark>');
    }

    return highlighted;
  }, [searchTerm, highlightSearchResults, caseSensitiveSearch]);

  const renderFormattedView = () => {
    const content = getFormattedYaml;
    const lines = content.split('\n');

    return (
      <div className="flex h-full border border-gray-300 rounded">
        {showLineNumbers && (
          <div className="bg-gray-50 border-r border-gray-300 px-3 py-2 text-right text-gray-500 font-mono text-xs min-w-12 select-none flex-shrink-0">
            {lines.map((_, index) => (
              <div key={index} className="leading-5 h-5">
                {index + 1}
              </div>
            ))}
          </div>
        )}
        <div className="flex-1 p-3 overflow-auto">
          <pre className="font-mono text-sm whitespace-pre-wrap">
            {lines.map((line, index) => (
              <div key={index} className="leading-5 h-5" dangerouslySetInnerHTML={{
                __html: highlightYamlLine(line)
              }} />
            ))}
          </pre>
        </div>
      </div>
    );
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    validateYaml(value);
  };

  const handleSortYaml = () => {
    if (parsedData) {
      const sortedData = sortObjectDeep(parsedData);
      const sortedString = toYaml(sortedData);
      setInput(sortedString);
      validateYaml(sortedString);
    }
  };

  const handleMinifyYaml = () => {
    if (parsedData) {
      const minified = getMinifiedYaml;
      setInput(minified);
      validateYaml(minified);
    }
  };

  const handleFormatYaml = () => {
    if (parsedData) {
      const formatted = getFormattedYaml;
      setInput(formatted);
      validateYaml(formatted);
    }
  };

  const navigateSearchResults = (direction: 'prev' | 'next') => {
    if (searchResults.length === 0) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (currentSearchIndex + 1) % searchResults.length;
    } else {
      newIndex = currentSearchIndex === 0 ? searchResults.length - 1 : currentSearchIndex - 1;
    }
    setCurrentSearchIndex(newIndex);

    // Expand path to show the current result
    const result = searchResults[newIndex];
    if (result) {
      const pathParts = result.path.split(/[.\[\]]+/).filter(Boolean);
      const newExpanded = new Set(expandedPaths);
      let currentPath = '';
      pathParts.forEach((part, index) => {
        if (index === 0) {
          currentPath = part;
        } else {
          currentPath += pathParts[index-1]?.match(/\d+/) ? `[${part}]` : `.${part}`;
        }
        newExpanded.add(currentPath);
      });
      setExpandedPaths(newExpanded);
    }
  };

  const handleCopy = async () => {
    const content = viewMode === 'formatted' ? getFormattedYaml : input;
    if (content) {
      try {
        await navigator.clipboard.writeText(content);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  const handleDownload = () => {
    const content = viewMode === 'formatted' ? getFormattedYaml : input;
    if (content) {
      const blob = new Blob([content], { type: 'application/x-yaml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'formatted.yaml';
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInput(content);
        validateYaml(content);
      };
      reader.readAsText(file);
    }
  };

  const handleClear = () => {
    setInput('');
    setParsedData(null);
    setIsValid(null);
    setError('');
    setExpandedPaths(new Set());
    setSearchTerm('');
    setSearchResults([]);
  };

  const loadSample = () => {
    const sample = `# Sample YAML Configuration
company:
  name: "TechCorp Inc."
  founded: "2020-01-15T10:30:00Z"
  website: "https://techcorp.example.com"
  email: "contact@techcorp.com"
  phone: "+1-555-123-4567"
  
  employees:
    - id: 1
      name: "John Doe"
      email: "john.doe@techcorp.com"
      phone: "+1-555-234-5678"
      department: "Engineering"
      salary: 75000.50
      isActive: true
      skills:
        - "JavaScript"
        - "React"
        - "Node.js"
        - "Python"
      joinDate: "2022-03-15T09:00:00Z"
      address:
        street: "123 Main St"
        city: "San Francisco"
        state: "CA"
        zipCode: "94105"
        coordinates:
          latitude: 37.7749
          longitude: -122.4194
      projects:
        - name: "Web Dashboard"
          status: "completed"
          priority: "high"
          deadline: "2024-03-15T23:59:59Z"
          budget: 50000
        - name: "Mobile App"
          status: "in-progress"
          priority: "medium"
          deadline: null
          budget: 75000.25
          
    - id: 2
      name: "Jane Smith"
      email: "jane.smith@techcorp.com"
      phone: "+1-555-345-6789"
      department: "Design"
      salary: 68000
      isActive: false
      skills:
        - "Figma"
        - "Photoshop"
        - "UI/UX"
        - "Sketch"
      joinDate: "2021-11-22T09:00:00Z"
      address:
        street: "456 Oak Ave"
        city: "Los Angeles"
        state: "CA"
        zipCode: "90210"
      projects: []

  departments:
    engineering:
      head: "Alice Johnson"
      budget: 500000
      headcount: 15
    design:
      head: "Bob Wilson"
      budget: 200000
      headcount: 8
    marketing:
      head: "Carol Brown"
      budget: 150000
      headcount: 5

  metadata:
    version: "2.1.0"
    lastUpdated: "2024-05-22T14:30:00Z"
    features:
      - "analytics"
      - "reporting"
      - "automation"
    settings:
      theme: "dark"
      notifications: true
      autoSave: false
      maxFileSize: 10485760`;
    
    setInput(sample);
    validateYaml(sample);
  };

  // Helper functions
  const countKeys = useCallback((obj: any, count = 0): number => {
    if (obj && typeof obj === 'object') {
      if (Array.isArray(obj)) {
        obj.forEach(item => {
          count = countKeys(item, count);
        });
      } else {
        count += Object.keys(obj).length;
        Object.values(obj).forEach(value => {
          count = countKeys(value, count);
        });
      }
    }
    return count;
  }, []);

  const getMaxDepth = useCallback((obj: any, depth = 0): number => {
    if (obj && typeof obj === 'object') {
      if (Array.isArray(obj)) {
        return Math.max(depth, ...obj.map(item => getMaxDepth(item, depth + 1)));
      } else {
        return Math.max(depth, ...Object.values(obj).map(value => getMaxDepth(value, depth + 1)));
      }
    }
    return depth;
  }, []);

  const getTypeDistribution = useCallback((obj: any): Record<string, number> => {
    const distribution: Record<string, number> = {};
    
    const countTypes = (value: any) => {
      const type = getAdvancedDataType(value);
      distribution[type] = (distribution[type] || 0) + 1;
      
      if (Array.isArray(value)) {
        value.forEach(item => countTypes(item));
      } else if (value && typeof value === 'object') {
        Object.values(value).forEach(val => countTypes(val));
      }
    };
    
    countTypes(obj);
    return distribution;
  }, [getAdvancedDataType]);

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-7xl mx-auto p-4">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Advanced YAML Formatter Pro</h1>
          <p className="text-gray-600">Professional YAML formatter with advanced search, filtering, and analytics</p>
        </div>

        {/* Main Toolbar */}
        <div className="bg-gray-50 border border-gray-300 rounded p-4 mb-4">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <button
              onClick={() => validateYaml(input)}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium transition-colors"
            >
              <Check className="w-4 h-4 inline mr-1" />
              Validate YAML
            </button>

            <button
              onClick={handleFormatYaml}
              disabled={!parsedData}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
            >
              <Maximize2 className="w-4 h-4 inline mr-1" />
              Format
            </button>

            <button
              onClick={handleMinifyYaml}
              disabled={!parsedData}
              className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 font-medium transition-colors"
            >
              <Minimize2 className="w-4 h-4 inline mr-1" />
              Minify
            </button>

            <button
              onClick={handleSortYaml}
              disabled={!parsedData}
              className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50 font-medium transition-colors"
            >
              <ArrowUpDown className="w-4 h-4 inline mr-1" />
              Sort YAML
            </button>
            
            <button
              onClick={handleCopy}
              disabled={!parsedData}
              className="px-4 py-2 bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-50 font-medium transition-colors"
            >
              <Copy className="w-4 h-4 inline mr-1" />
              {copied ? 'Copied!' : 'Copy'}
            </button>

            <button
              onClick={handleDownload}
              disabled={!parsedData}
              className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 font-medium transition-colors"
            >
              <Download className="w-4 h-4 inline mr-1" />
              Download
            </button>

            <label className="px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700 cursor-pointer font-medium transition-colors">
              <Upload className="w-4 h-4 inline mr-1" />
              Upload File
              <input
                type="file"
                accept=".yaml,.yml,.txt"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>

            <button
              onClick={handleClear}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-medium transition-colors"
            >
              <RefreshCw className="w-4 h-4 inline mr-1" />
              Clear
            </button>

            <button
              onClick={loadSample}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 font-medium transition-colors"
            >
              <Zap className="w-4 h-4 inline mr-1" />
              Sample
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">View:</label>
              <select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value as ViewMode)}
                className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="tree">Tree View</option>
                <option value="formatted">Formatted View</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Indent:</label>
              <select
                value={indentSize}
                onChange={(e) => setIndentSize(Number(e.target.value))}
                className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value={2}>2 spaces</option>
                <option value={4}>4 spaces</option>
                <option value={8}>8 spaces</option>
                <option value={0}>Compact</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium">Filter:</label>
              <select
                value={filterByType}
                onChange={(e) => setFilterByType(e.target.value)}
                className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="string">Strings</option>
                <option value="number">Numbers</option>
                <option value="boolean">Booleans</option>
                <option value="array">Arrays</option>
                <option value="object">Objects</option>
                <option value="email">Emails</option>
                <option value="url">URLs</option>
                <option value="date">Dates</option>
                <option value="phone">Phone Numbers</option>
              </select>
            </div>

            {viewMode === 'tree' && parsedData && (
              <div className="flex gap-2">
                <button
                  onClick={expandAll}
                  className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 transition-colors"
                >
                  <Eye className="w-3 h-3 inline mr-1" />
                  Expand All
                </button>
                <button
                  onClick={collapseAll}
                  className="px-3 py-1 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 transition-colors"
                >
                  <EyeOff className="w-3 h-3 inline mr-1" />
                  Collapse All
                </button>
              </div>
            )}
          </div>

          {/* Advanced Options */}
          <div className="border-t border-gray-300 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Settings className="w-4 h-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">Advanced Options</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showLineNumbers}
                  onChange={(e) => setShowLineNumbers(e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                Line Numbers
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showDataTypes}
                  onChange={(e) => setShowDataTypes(e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                Data Types
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={showQuotes}
                  onChange={(e) => setShowQuotes(e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                Force Quotes
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={highlightSyntax}
                  onChange={(e) => setHighlightSyntax(e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                Syntax Highlight
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={compactArrays}
                  onChange={(e) => setCompactArrays(e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                Compact Arrays
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={highlightSearchResults}
                  onChange={(e) => setHighlightSearchResults(e.target.checked)}
                  className="w-4 h-4 text-blue-600"
                />
                Highlight Results
              </label>
            </div>
          </div>
        </div>

        {/* Search Panel */}
        <div className="bg-white border border-gray-300 rounded p-4 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-5 h-5 text-gray-600" />
            <span className="text-sm font-medium text-gray-700">Advanced Search & Filter</span>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search keys and values..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
                {searchResults.length > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => navigateSearchResults('prev')}
                      className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                      title="Previous result"
                    >
                      ←
                    </button>
                    <span className="text-sm text-gray-600 min-w-20 text-center">
                      {currentSearchIndex + 1} of {searchResults.length}
                    </span>
                    <button
                      onClick={() => navigateSearchResults('next')}
                      className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                      title="Next result"
                    >
                      →
                    </button>
                  </div>
                )}
              </div>
              
              <div className="flex flex-wrap gap-3 mt-3">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={caseSensitiveSearch}
                    onChange={(e) => setCaseSensitiveSearch(e.target.checked)}
                    className="w-4 h-4 text-blue-600"
                  />
                  Case Sensitive
                </label>
                
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={searchInKeys}
                    onChange={(e) => setSearchInKeys(e.target.checked)}
                    className="w-4 h-4 text-blue-600"
                  />
                  Search in Keys
                </label>
                
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={searchInValues}
                    onChange={(e) => setSearchInValues(e.target.checked)}
                    className="w-4 h-4 text-blue-600"
                  />
                  Search in Values
                </label>
              </div>
            </div>
            
            <div>
              <div className="text-sm text-gray-600 space-y-1">
                {searchResults.length > 0 ? (
                  <>
                    <div className="font-medium">Search Results: {searchResults.length}</div>
                    <div className="max-h-20 overflow-y-auto text-xs">
                      {searchResults.slice(0, 5).map((result, index) => (
                        <div key={index} className="truncate">
                          {result.path}: {String(result.value).substring(0, 30)}...
                        </div>
                      ))}
                      {searchResults.length > 5 && (
                        <div className="text-gray-500">... and {searchResults.length - 5} more</div>
                      )}
                    </div>
                  </>
                ) : searchTerm ? (
                  <div>No results found</div>
                ) : (
                  <div>Enter search term to find matches</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* Input Panel */}
          <div>
            <div className="bg-gray-100 px-4 py-2 border border-gray-300 rounded-t font-medium text-gray-700 flex items-center justify-between">
              <span>YAML Input</span>
              {isValid !== null && (
                <div className="flex items-center gap-2">
                  {isValid ? (
                    <>
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-green-600 text-sm">Valid YAML</span>
                    </>
                  ) : (
                    <>
                      <X className="w-4 h-4 text-red-600" />
                      <span className="text-red-600 text-sm">Invalid YAML</span>
                    </>
                  )}
                </div>
              )}
            </div>
            <textarea
              value={input}
              onChange={(e) => handleInputChange(e.target.value)}
              placeholder="Paste YAML here..."
              className="w-full p-3 border border-gray-300 border-t-0 rounded-b font-mono text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ height: '600px' }}
            />
            {error && (
              <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-4 h-4" />
                  <span className="font-medium">Error:</span>
                </div>
                <p className="text-red-600 text-sm mt-1 font-mono">{error}</p>
              </div>
            )}
          </div>

          {/* Output Panel */}
          <div>
            <div className="bg-gray-100 px-4 py-2 border border-gray-300 rounded-t font-medium text-gray-700 flex items-center justify-between">
              <span>{viewMode === 'tree' ? 'Tree View' : 'Formatted YAML'}</span>
              {parsedData && (
                <span className="text-sm text-gray-500">
                  {viewMode === 'tree' ? 'Interactive Explorer' : `${getFormattedYaml.split('\n').length} lines`}
                </span>
              )}
            </div>
            <div className="border border-gray-300 border-t-0 rounded-b bg-white overflow-hidden" style={{ height: '600px' }}>
              {!parsedData ? (
                <div className="flex items-center justify-center h-full text-gray-500">
                  <div className="text-center">
                    <FileText className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>Output will appear here...</p>
                    <p className="text-xs text-gray-400 mt-1">Paste valid YAML to get started</p>
                  </div>
                </div>
              ) : viewMode === 'tree' ? (
                renderTreeView()
              ) : (
                <div className="h-full overflow-auto">
                  {renderFormattedView()}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Analytics Dashboard */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Statistics Panel */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-blue-600" />
              YAML Analytics
            </h3>
            {parsedData ? (
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Size:</span>
                  <span className="font-medium">{new Blob([getFormattedYaml]).size} bytes</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Characters:</span>
                  <span className="font-medium">{getFormattedYaml.length.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Lines:</span>
                  <span className="font-medium">{getFormattedYaml.split('\n').length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Keys:</span>
                  <span className="font-medium">{countKeys(parsedData)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Max Depth:</span>
                  <span className="font-medium">{getMaxDepth(parsedData)}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Search Results:</span>
                  <span className="font-medium">{searchResults.length}</span>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Load YAML to see analytics</p>
            )}
          </div>

          {/* Type Distribution */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Type className="w-5 h-5 text-purple-600" />
              Data Types
            </h3>
            {parsedData ? (
              <div className="space-y-2">
                {Object.entries(getTypeDistribution(parsedData)).map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${getTypeColor(type as DataType)}`}>
                        {getTypeIcon(type as DataType)}
                        {type}
                      </span>
                    </div>
                    <span className="font-medium">{count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Load YAML to see type distribution</p>
            )}
          </div>

          {/* Quick Actions */}
          <div className="bg-white border border-gray-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-yellow-600" />
              Quick Actions
            </h3>
            <div className="space-y-3">
              <button
                onClick={() => setSearchTerm('email')}
                className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded transition-colors"
              >
                <Mail className="w-4 h-4 inline mr-2" />
                Find all emails
              </button>
              <button
                onClick={() => setSearchTerm('http')}
                className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded transition-colors"
              >
                <Link className="w-4 h-4 inline mr-2" />
                Find all URLs
              </button>
              <button
                onClick={() => setFilterByType('date')}
                className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded transition-colors"
              >
                <Calendar className="w-4 h-4 inline mr-2" />
                Show only dates
              </button>
              <button
                onClick={() => setFilterByType('number')}
                className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded transition-colors"
              >
                <Hash className="w-4 h-4 inline mr-2" />
                Show only numbers
              </button>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setFilterByType('all');
                }}
                className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded transition-colors"
              >
                <Eye className="w-4 h-4 inline mr-2" />
                Reset all filters
              </button>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-gray-200">
          <h3 className="text-lg font-semibold mb-4 text-center">🚀 Professional YAML Features</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Real-time YAML Validation
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Interactive Tree View
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Advanced Search & Navigation
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Smart Data Type Detection
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Type-based Filtering
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              YAML Analytics Dashboard
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Sort, Format & Minify
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Email, URL, Date Detection
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Case-sensitive Search
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Search Result Highlighting
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              File Upload/Download
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              YAML-specific Syntax
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Comment Preservation
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Multi-line String Support
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Array & Object Indicators
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-green-600" />
              Professional UI/UX
            </div>
          </div>
        </div>

        {/* YAML-specific Help */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-semibold text-blue-900 mb-2">💡 YAML Tips & Syntax</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
            <div>
              <strong>Basic Syntax:</strong>
              <ul className="mt-1 space-y-1 text-xs">
                <li>• Use spaces for indentation (not tabs)</li>
                <li>• Colons separate keys and values</li>
                <li>• Hyphens indicate array items</li>
                <li>• Comments start with #</li>
              </ul>
            </div>
            <div>
              <strong>Data Types:</strong>
              <ul className="mt-1 space-y-1 text-xs">
                <li>• Strings: plain text or "quoted"</li>
                <li>• Numbers: 123 or 45.67</li>
                <li>• Booleans: true/false</li>
                <li>• Null values: null or ~</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default YamlFormatter;